import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ToshibaContext } from '../toshiba.context';
import { KPI_CODE } from '../../enum/toshiba.enum';
import { processUnit } from '../../utils/farecast-util';
import { isNil } from 'lodash';
import { DATA_UNIT } from '../../enum/common.enum';
import APIList from '../../http/ApiList';
import dayjs from 'dayjs';
import { AppContext } from '../AppContext';
import { useSessionStorageState } from 'ahooks';

type SegmentInfo = {
  segment: string;
  type: 'actual' | 'forecast';
};

// 画面 segment 排序 map
export const segmentSortSeqMap = {
  原子力: 0,
  火力: 1,
  水力: 2,
  調整: 10,
};

// 画面 BU 排序 map
export const buSortMap = {
  原子力: {
    '廃止・再処理': 0,
    国内軽水炉: 1,
    新技術: 2,
    国内: 3,
    海外: 4,
    その他: 5,
    共通: 6,
    調整: 10,
  },
  火力: {
    国内建設: 0,
    国内サービス: 1,
    海外建設: 2,
    海外サービス: 3,
    その他: 4,
    共通: 5,
    調整: 10,
  },
  水力: {
    国内: 0,
    海外: 1,
    その他: 2,
    共通: 3,
    調整: 10,
  },
};

const ToshibaProvider = ({ children }) => {
  const { color, getCalenderMap } = useContext(AppContext);

  const [snapshot, setSnapshot] = useSessionStorageState<any>('snapshot');

  const [drillFields, setDrillFields] = useSessionStorageState<any>('drillFields', {
    defaultValue: [],
  });

  const [filter, setFilter] = useSessionStorageState<any>('filter', {
    defaultValue: {},
  });

  useEffect(() => {
    APIList.getVersion()
      .post({})
      .then(({ currentSnapshot, versions }) => {
        if (!snapshot || snapshot.versions.length === 0 || currentSnapshot !== snapshot.versions[0].snapshot) {
          setSnapshot({
            currentSnapshot,
            versions,
          });
        }
      });
  }, []);

  const kpiRule: any = useMemo(
    () => ({
      default: {
        unitValue: 1,
        unitLabel: '円',
        precision: 2,
        mark: '',
      },
      [KPI_CODE.SALES_AMOUNT]: {
        unitValue: 100000000,
        unitLabel: '億円',
        precision: 0,
      },
      [KPI_CODE.DIFF_RATE]: {
        unitValue: DATA_UNIT.PERCENTAGE,
        unitLabel: '億円',
        precision: 0,
      },
    }),
    []
  );

  const kpiValue = useCallback((kpi: KPI_CODE, value: number, isDiff = false, settingUnitPrecisionMap = {}) => {
    if (isNil(value) || Number.isNaN(value)) {
      return '-';
    }
    const map = {
      ...kpiRule,
      ...settingUnitPrecisionMap,
    };
    const { unitValue, precision = 2, mark = '' } = map[kpi] ? map[kpi] : map['default'];
    let num = processUnit(value, unitValue, precision);
    // 解决 -0 问题
    if (num === 0) {
      num = Math.abs(num);
    }
    if (DATA_UNIT.PERCENTAGE === unitValue) {
      if (isDiff) {
        let c = null;
        switch (true) {
          case num > 0:
            c = color.successColor;
            break;
          case num < 0:
            c = color.errorColor;
            break;
          default:
            break;
        }
        return <span style={{ color: c }}>{`${mark} ${num > 0 ? '+' : ''}${num.toLocaleString()}%`}</span>;
        // return <span style={{ color: c }}>{`${mark} ${num > 0 ? '+' : ''}${num.toLocaleString()}`}</span>;
      }
      return `${mark} ${num.toLocaleString()}%`;
    }
    if (isDiff) {
      let c = null;
      switch (true) {
        case num > 0:
          c = color.successColor;
          break;
        case num < 0:
          c = color.errorColor;
          break;
        default:
          break;
      }
      return <span style={{ color: c }}>{`${mark} ${num > 0 ? '+' : ''}${num.toLocaleString()}`}</span>;
    }
    return `${mark} ${num.toLocaleString()}`;
  }, []);

  /**
   * 获取KPI 单位的Label
   * @param kpi
   * @param settingUnitPrecisionMap
   * @returns
   */
  const kpiUnitLabel = useCallback((kpi: KPI_CODE, settingUnitPrecisionMap = {}, needBracket = true) => {
    const map = {
      ...kpiRule,
      ...settingUnitPrecisionMap,
    };
    const { unitLabel } = map[kpi] ? map[kpi] : map['default'];
    if (unitLabel) {
      if (needBracket) {
        return `(${unitLabel})`;
      } else {
        return `${unitLabel}`;
      }
    }
    return '';
  }, []);

  /**
   * 通过日历获取集計期間
   */
  const getAggregationPeriod = useCallback(
    (snapshot, fiscalYear, evaluationPeriod) => {
      const calenderMap = getCalenderMap(snapshot);
      if (calenderMap && calenderMap[fiscalYear]) {
        return calenderMap[fiscalYear][evaluationPeriod].period;
      }
      return null;
    },
    [getCalenderMap]
  );

  return (
    <ToshibaContext.Provider
      value={{
        kpiValue,
        kpiUnitLabel,
        snapshot,
        setSnapshot,
        drillFields,
        setDrillFields,
        filter,
        setFilter,
        getAggregationPeriod,
      }}
    >
      {children}
    </ToshibaContext.Provider>
  );
};

export default ToshibaProvider;
